import type { Handler } from './handler';
import type { ApiFetchDailyCheckinPort } from './ports/api-fetch-daily-checkin.port';
import { USE_MOCK, readMockBoard } from './daily-checkin.mock';
import type { DailyCheckinBoard } from '~/types/daily-checkin';
import { tryCatch } from '~/utils/apostatudo/try-catch';
import { resolveAdapter } from '~/adapters/registry';

// GET /daily-check-in/board. O core limita a 120 req/min; este cache de 5 min é
// o que garante que abrir e fechar o modal não encoste no teto.
const CACHE_TTL_MS = 5 * 60 * 1000;

// Guardado COM o `userId`: no SPA dá pra trocar de conta sem recarregar a
// página, e um board é o estado de um jogador específico.
let cache: {
  userId: number;
  data: DailyCheckinBoard;
  cachedAt: number;
} | null = null;

// Board guardado deste jogador, de qualquer idade: o caminho de falha serve o
// board velho de propósito, que é melhor que tela vazia.
function cachedFor(userId: number): DailyCheckinBoard | null {
  return cache?.userId === userId ? cache.data : null;
}

function isCacheFresh(): boolean {
  return !!cache && Date.now() - cache.cachedAt < CACHE_TTL_MS;
}

// Requisição em vôo, pra duas chamadas quase simultâneas compartilharem a MESMA
// resposta: o hub aquece a trilha ao abrir e o jogador pode clicar no card antes
// de ela chegar. Sem isso, o clique dispararia um segundo GET do mesmo board.
let inflight: { userId: number; promise: Promise<DailyCheckinBoard> } | null =
  null;

// Board vazio = "não há período no ar" (o modal mostra o aviso de
// indisponível). É também o que devolvemos quando a API falha e não há nada
// guardado — melhor avisar que não tem jogo do que pintar uma trilha errada.
const EMPTY: DailyCheckinBoard = {
  period: null,
  // Inelegível, pela mesma regra do mapper: elegibilidade só existe quando o
  // core diz `is_eligible` verdadeiro, e num board sintético ninguém disse nada.
  // Vale pros dois usos deste EMPTY — sem período no ar e API fora —, então uma
  // falha de rede também esconde o item do menu horizontal em vez de convidar
  // pra uma promoção que talvez não seja deste jogador.
  isEligible: false,
  canClaimToday: false,
  nextClaimAvailableAt: null,
  claimedCount: 0,
  remainingCount: 0,
  missedDays: 0,
  rewards: [],
};

export const fetchDailyCheckinHandler: Handler<Params, Ports, Result> = async (
  params,
  ports = {
    apiFetchDailyCheckin: resolveAdapter('apiFetchDailyCheckin'),
  },
) => {
  if (USE_MOCK) {
    return readMockBoard();
  }

  const cached = cachedFor(params.userId);

  if (cached && isCacheFresh()) {
    return cached;
  }

  let promise =
    inflight?.userId === params.userId ? inflight.promise : undefined;

  if (!promise) {
    promise = ports.apiFetchDailyCheckin.handle({ userId: params.userId });
    inflight = { userId: params.userId, promise };
  }

  const [board, error] = await tryCatch(promise);

  // Só limpa se ninguém tiver começado outra no meio (ex.: refresh do resgate).
  if (inflight?.promise === promise) inflight = null;

  if (!error && board) {
    // "Sem período no ar" NÃO entra no cache: é justamente a resposta que muda
    // quando a operação publica um período, e segurá-la por 5 minutos faria o
    // jogador continuar vendo "indisponível" com o jogo já no ar. Consultar de
    // novo custa 1 request por abertura, longe do teto de 120/min.
    cache = board.period
      ? { userId: params.userId, data: board, cachedAt: Date.now() }
      : null;

    return board;
  }

  console.error('[fetchDailyCheckinHandler] falha ao buscar:', error);

  return cached ?? EMPTY;
};

export function clearDailyCheckinCache(): void {
  cache = null;
  // A requisição em vôo também é dado velho depois de um resgate: quem chamar em
  // seguida tem que pedir de novo, não pegar carona na resposta anterior.
  inflight = null;
}

type Params = { userId: number };

type Ports = { apiFetchDailyCheckin: ApiFetchDailyCheckinPort };

type Result = Promise<DailyCheckinBoard>;
